import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { FleetPage, FleetSection, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../shared/table-builder/table-ag-client'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { IconList } from '../../../../shared/icon/icon'
import { useRoleSysBilling } from '../../../../role/hook/role-sys'
import { getEntitlements, recompute, billingAvailable } from './billing-api'
import { BillingUnavailable } from './unavailable'
import { numberText } from './format'

const rowsOf = computed => {
  const features = (computed && computed.features) || {}
  const provenance = (computed && computed.provenance) || {}
  return Object.keys(features).sort().map(key => ({
    id: key,
    feature: key,
    on: features[key].on ? 'On' : 'Off',
    limit: numberText(features[key].limit),
    included: numberText(features[key].included),
    from: provenance[key] || '—'
  }))
}

const getRowId = params => params.data.id
const NONE = { Icon: IconList, title: 'No features', message: 'Nothing is computed for this organization.' }

const EntitlementsView = ({ organizationID }) => {
  const role = useRoleSysBilling()
  const tableRef = useRef()
  const [data, setData] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)

  const load = useCallback(async () => {
    try {
      const v = await getEntitlements(organizationID)
      setError(null)
      setData(v)
    } catch (err) {
      setData(null)
      setError(serverMessage(err, "The entitlements couldn't be loaded."), err)
    }
  }, [organizationID, setError])

  useEffect(() => { setData(null); setNotice(null); load() }, [load])

  const runRecompute = async () => {
    setBusy(true)
    setNotice(null)
    try {
      await recompute([Number(organizationID)])
      setNotice('Recomputed and stored.')
      await load()
    } catch (err) {
      setError(serverMessage(err, "The recompute didn't run."), err)
    } finally {
      setBusy(false)
    }
  }

  const computed = data && data.computed
  const apiList = useMemo(() => ({ items: rowsOf(computed), loaded: true, loading: false, error: undefined }), [computed])

  if (error) return <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>
  if (!data) return <p className="sw-hint">Loading…</p>
  if (data.skipped) return <Notice tone="warn" role="status">This organization was deleted, so nothing is computed for it.</Notice>

  const plan = computed.plan || {}
  return (
    <>
      {notice && <p className="text-success small" role="status">{notice}</p>}
      <FleetSection id="bl-plan" title="Plan"
        actions={role.accessUpdate ? <button type="button" className="btn sw-btn-primary" disabled={busy} onClick={runRecompute}>Recompute</button> : null}>
        <dl className="sw-facts">
          <dt>Plan</dt><dd>{plan.key}</dd>
          <dt>Source</dt><dd>{plan.source}</dd>
          <dt>Status</dt><dd>{plan.status}</dd>
        </dl>
        <p className="mb-0">{data.matchesStored ? 'Matches stored' : 'Differs from stored'}</p>
      </FleetSection>
      <FleetGrid title="entitlements" tableRef={tableRef} exportName="billing-entitlements">
        <TableAgClient ref={tableRef} apiList={apiList} queryKey="" sortCol="feature" getRowId={getRowId} emptyState={NONE}>
          <ColumnFormat field="feature" header="Feature" sortable={false} minWidth={200} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="on" header="On" sortable={false} minWidth={90} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="limit" header="Limit" sortable={false} minWidth={100} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="included" header="Included" sortable={false} minWidth={100} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="from" header="From" sortable={false} minWidth={120} render={r => (r.data ? r.value : '')} />
        </TableAgClient>
      </FleetGrid>
    </>
  )
}

const EntitlementsLoader = () => {
  const { organizationID } = useParams()
  const history = useHistory()
  return (
    <FleetPage title="Organization entitlements" purpose="What an organization's plan, contract and exceptions come to now, beside what is stored.">
      <Notice tone="warn">Shadow mode: plans don't decide access yet.</Notice>
      <div className="mb-3">
        <OrgPicker id="bl-org" label="Organization" value={organizationID || ''} small
          onChange={id => history.push(id ? `/admin/billing/organizations/${encodeURIComponent(id)}` : '/admin/billing/organizations')} />
      </div>
      {organizationID && <EntitlementsView organizationID={organizationID} />}
    </FleetPage>
  )
}

export const EntitlementsPage = () => (billingAvailable() ? <EntitlementsLoader /> : <BillingUnavailable title="Organization entitlements" />)
